import { inject, Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  collectionData,
  writeBatch,
  getDoc
} from '@angular/fire/firestore';
import { AuthService } from './auth-service';
import { BehaviorSubject, distinctUntilChanged, from, map, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import { SelectedProduct } from '../models/selected-product';
import { ProductService } from './product-service';
import { OrderService } from './order-service';

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private firestore = inject(Firestore);
  private authService = inject(AuthService);
  private orderService = inject(OrderService)
  private cartSubject = new BehaviorSubject<SelectedProduct[] | null>(null);
  cart$ = this.cartSubject.asObservable();

  constructor() {
    //  Auto-load cart when user logs in
    this.authService.user$
      .pipe(
        distinctUntilChanged((a, b) => a?.id === b?.id), // prevent same user trigger
        switchMap(user => {
          if (user) {
            const cartRef = collection(this.firestore, `users/${user.id}/cart`);
            return collectionData(cartRef, { idField: 'id' });
          } else {
            return of([]);
          }
        }),
      ).pipe(take(1))
      .subscribe(items => {
        this.cartSubject.next(items as SelectedProduct[]);
      });
  }

  get currentCart(): SelectedProduct[] {
    return this.cartSubject.value ?? [];
  }

  /**
   *  Add or update a selected product in user's cart
   */
  // addToCart(selectedProduct: SelectedProduct) {
  //   return this.authService.user$.pipe(
  //     take(1),
  //     switchMap(user => {
  //       if (!user) return throwError(() => new Error('User not logged in'));

  //       return from((async () => {
  //         const cartRef = collection(this.firestore, `users/${user.id}/cart`);

  //         const conditions: any[] = [
  //           where('product.id', '==', selectedProduct.product.id),
  //           where('color', '==', selectedProduct.color)
  //         ];

  //         const cleanProduct: any = { ...selectedProduct };
  //         // Add size condition ONLY if it exists
  //         if (selectedProduct.size) {
  //           conditions.push(where('size', '==', selectedProduct.size));
  //         } else {
  //           delete cleanProduct.size;
  //         }

  //         const q = query(cartRef, ...conditions);
  //         const snapshot = await getDocs(q);

  //         if (!snapshot.empty) {
  //           // Update existing item quantity
  //           const existingDoc = snapshot.docs[0];
  //           const existingItem = existingDoc.data() as SelectedProduct;
  //           const newQuantity = existingItem.quantity + selectedProduct.quantity;
  //           await updateDoc(existingDoc.ref, { quantity: newQuantity });
  //         } else {
  //           // Add as new selected product
  //           await addDoc(cartRef, cleanProduct);
  //         }

  //         this.refreshLocalCart();
  //       })());
  //     })
  //   );
  // }

  addToCart(selectedProduct: SelectedProduct) {
    return this.authService.user$.pipe(
      take(1),
      switchMap(user => {
        if (!user) {
          return throwError(() => new Error('User not logged in'));
        }

        const cartRef = collection(this.firestore, `users/${user.id}/cart`);

        const conditions: any[] = [
          where('product.id', '==', selectedProduct.product.id),
          where('color', '==', selectedProduct.color)
        ];

        const cleanProduct: any = { ...selectedProduct };

        if (selectedProduct.size) {
          conditions.push(where('size', '==', selectedProduct.size));
        } else {
          delete cleanProduct.size;
        }

        const q = query(cartRef, ...conditions);
        //  STEP 1: Get existing cart item
        return from(getDocs(q)).pipe(
          switchMap(snapshot => {
            let finalQuantity = selectedProduct.quantity;

            if (!snapshot.empty) {
              const existingItem = snapshot.docs[0].data() as SelectedProduct;
              finalQuantity = existingItem.quantity + selectedProduct.quantity;
            }

            //  STEP 2: Check stock with FINAL quantity
            const itemToCheck: SelectedProduct = {
              ...selectedProduct,
              quantity: finalQuantity
            };

            return this.orderService.checkProductAvailability(itemToCheck).pipe(
              switchMap(result => {
                if (!result.available) {
                  throw new Error(result.message || 'Out of stock');
                }

                //  STEP 3: Update or Insert
                if (!snapshot.empty) {
                  const existingDoc = snapshot.docs[0];

                  return from(
                    updateDoc(existingDoc.ref, {
                      quantity: finalQuantity
                    })
                  );
                } else {
                  return from(addDoc(cartRef, cleanProduct));
                }
              })
            );
          })
        );
      }),
      tap(() => this.refreshLocalCart())
    );
  }


  /**
   *  Remove product from cart
   */
  removeFromCart(itemId: string) {
    return this.authService.user$.pipe(
      take(1),
      switchMap(async user => {
        if (!user) throw new Error('User not logged in');
        const itemRef = doc(this.firestore, `users/${user.id}/cart/${itemId}`);
        await deleteDoc(itemRef);
        this.refreshLocalCart();
      })
    );
  }

  /**
   *  Update quantity of a product
   */
  // updateQuantity(itemId: string, quantity: number) {
  //   return this.authService.user$.pipe(
  //     take(1),
  //     switchMap(async user => {
  //       if (!user) throw new Error('User not logged in');
  //       const itemRef = doc(this.firestore, `users/${user.id}/cart/${itemId}`);
  //       await updateDoc(itemRef, { quantity });
  //       this.refreshLocalCart();
  //     })
  //   );
  // }

  updateQuantity(itemId: string, item: SelectedProduct) {
    return this.authService.user$.pipe(
      take(1),
      switchMap(user => {
        if (!user) throw new Error('User not logged in');

        // STEP 1: Check stock first
        return this.orderService.checkProductAvailability(item).pipe(
          switchMap(result => {
            if (!result.available) {
              throw new Error(result.message || 'Out of stock');
            }

            // STEP 2: Update cart
            const itemRef = doc(
              this.firestore,
              `users/${user.id}/cart/${itemId}`
            );

            return from(
              updateDoc(itemRef, { quantity: item.quantity })
            );
          })
        );
      }),
      tap(() => this.refreshLocalCart())
    );
  }

  /**
   *  Clear entire cart
   */
  clearCart() {
    return this.authService.user$.pipe(
      take(1),
      switchMap(async user => {
        if (!user) throw new Error('User not logged in');
        const cartRef = collection(this.firestore, `users/${user.id}/cart`);
        const snapshot = await getDocs(cartRef);

        const batch = writeBatch(this.firestore);
        snapshot.docs.forEach(docSnap => batch.delete(docSnap.ref));
        await batch.commit();

        // this.cartSubject.next([]);
        this.refreshLocalCart();
      })
    );
  }

  /**
   *  Calculate total price
   */
  calculateTotal(): number {
    return this.currentCart.reduce(
      (sum, item) => sum + (item.price * item.quantity),
      0
    );
  }

  /**
   *  Refresh local cart
   */
  private refreshLocalCart() {
    this.authService.user$
      .pipe(
        take(1),
        switchMap(user => {
          if (!user) return of([]);
          const cartRef = collection(this.firestore, `users/${user.id}/cart`);
          return collectionData(cartRef, { idField: 'id' });
        })
      ).pipe(take(1))
      .subscribe(items => {
        this.cartSubject.next(items as SelectedProduct[]);
      });
  }

  /**
 * Update an existing cart item (quantity, color, size, etc.)
 */
  updateCartItem(itemId: string, updates: Partial<SelectedProduct>) {
    return this.authService.user$.pipe(
      take(1),
      switchMap(async user => {
        if (!user) throw new Error('User not logged in');

        const itemRef = doc(this.firestore, `users/${user.id}/cart/${itemId}`);

        // Update only provided fields (partial update)
        await updateDoc(itemRef, updates);

        // Refresh local cart data
        // this.refreshLocalCart();
      })
    );
  }


}
